import { useState } from "react";
import {
  Lock,
  CalendarBlank,
  WhatsappLogo,
  Envelope,
  Phone,
  SignOut,
  PaperPlaneTilt,
  CheckCircle,
} from "@phosphor-icons/react";
import { signOut } from "firebase/auth";
import { useDispatch, useSelector } from "react-redux";
import { auth } from "../../firebase";
import {
  logout,
  selectClinicProfile,
  selectTherapistProfile,
  selectUser,
} from "../../store/slices/authSlice";
import { useCreateLeadFromLandingPageMutation } from "../../store/services/nivApi";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./TrialContactModal.css";

const SUPPORT_WHATSAPP = "972543073737";
const SUPPORT_EMAIL = "office@clinikit.co.il";
const SUPPORT_PHONE_DISPLAY = "054-3073737";

function getDaysText(daysRemaining) {
  if (daysRemaining === 1) return "יום אחד";
  if (daysRemaining === 2) return "יומיים";
  return `${daysRemaining} ימים`;
}

/**
 * Unified trial contact popup — shown when the trial is about to end
 * (variant="warning", dismissible), when it has already ended / the
 * subscription lapsed (variant="expired", blocking), or when the clinic was
 * deactivated by the system admin (variant="disabled", blocking).
 *
 * All variants share the exact same design and contact options:
 *   - WhatsApp button
 *   - Email button
 *   - "leave your details" free-text box → niv_call_requests (source: trial_popup / disabled_popup)
 *
 * There are no self-serve payment links anymore (the Grow checkout was removed).
 */
function TrialContactModal({ variant = "expired", daysRemaining, therapistName, onAcknowledge }) {
  const dispatch = useDispatch();
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const user = useSelector(selectUser);

  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [createLead, { isLoading }] = useCreateLeadFromLandingPageMutation();

  const isExpired = variant === "expired";
  const isDisabled = variant === "disabled";
  const isBlocking = isExpired || isDisabled;
  const firstName = therapistName ? therapistName.split(" ")[0] : "";

  const contactPhone = therapistProfile?.phone || user?.phoneNumber || "";
  const clinicName = clinicProfile?.name || "";
  const contactEmail = user?.email || "";

  const whatsappContext = isDisabled
    ? "הגישה לחשבון הקליניקה שלי הושהתה"
    : isExpired
      ? "תקופת הניסיון שלי הסתיימה"
      : `תקופת הניסיון שלי מסתיימת בעוד ${getDaysText(daysRemaining)}`;
  const whatsappMessage = encodeURIComponent(
    `שלום! אני ${therapistName || "מטפל/ת"} מ-CliniKit. ${whatsappContext} ואשמח להמשיך להשתמש במערכת. אשמח לשמוע על האפשרויות.`
  );
  const whatsappLink = `https://wa.me/${SUPPORT_WHATSAPP}?text=${whatsappMessage}`;

  const emailSubject = encodeURIComponent("המשך שימוש ב-CliniKit");
  const emailBody = encodeURIComponent(
    `שלום,\n\nאני ${therapistName || "מטפל/ת"} ${whatsappContext}.\nאשמח לשמוע על האפשרויות להמשיך להשתמש במערכת.\n\nתודה!`
  );
  const emailLink = `mailto:${SUPPORT_EMAIL}?subject=${emailSubject}&body=${emailBody}`;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      dispatch(logout());
    } catch (error) {
      console.error("[CK-1052] Error signing out from trial popup:", {
        errorCode: "CK-1052",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleCallbackSubmit = async () => {
    setSubmitError("");
    try {
      await createLead({
        fullName: therapistName || null,
        phone: contactPhone,
        clinicName: clinicName || null,
        email: contactEmail || null,
        message: message.trim() || null,
        source: isDisabled ? "disabled_popup" : "trial_popup",
      }).unwrap();
      setSubmitted(true);
    } catch (error) {
      console.error("[CK-1053] Error submitting trial callback request:", {
        errorCode: "CK-1053",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      setSubmitError("אירעה שגיאה בשליחת הבקשה. נסו שוב או צרו קשר בוואטסאפ.");
    }
  };

  const handleOverlayClick = () => {
    if (!isBlocking && onAcknowledge) onAcknowledge();
  };

  return (
    <div
      className={`trial-contact-overlay ${isBlocking ? "is-blocking" : ""}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="trial-contact-modal" onClick={(e) => e.stopPropagation()}>
        {!isBlocking && onAcknowledge && <ModalCloseButton onClick={onAcknowledge} />}

        <div className="trial-contact-icon">
          {isBlocking ? (
            <Lock size={28} weight="duotone" />
          ) : (
            <CalendarBlank size={28} weight="duotone" />
          )}
        </div>

        <h2 className="trial-contact-title">
          {isDisabled ? (
            <>הגישה למערכת הושהתה</>
          ) : isExpired ? (
            <>תקופת הניסיון הסתיימה</>
          ) : (
            <>
              תקופת הניסיון מסתיימת
              <br />
              <span className="trial-contact-gradient">בעוד {getDaysText(daysRemaining)}</span>
            </>
          )}
        </h2>

        <p className="trial-contact-text">
          {firstName ? `${firstName}, ` : ""}
          {isDisabled
            ? "הגישה לחשבון הקליניקה הושהתה באופן זמני. כדי לחדש את הפעילות במערכת, צרו איתנו קשר ונשמח לעזור."
            : isExpired
              ? "כדי להמשיך לנהל את הקליניקה ולקבל גישה מלאה למערכת, צרו איתנו קשר ונתאים לכם את התוכנית המתאימה."
              : "כדי להמשיך להשתמש ב-CliniKit ולקבל גישה מלאה למערכת, צרו איתנו קשר ונתאים לכם את התוכנית המתאימה."}
        </p>

        <div className="trial-contact-actions">
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="trial-contact-btn trial-contact-btn-primary"
          >
            <WhatsappLogo size={20} weight="fill" />
            <span>דברו איתנו בוואטסאפ</span>
          </a>

          <a href={emailLink} className="trial-contact-btn trial-contact-btn-secondary">
            <Envelope size={18} weight="regular" />
            <span>שלחו מייל</span>
          </a>
        </div>

        {/* "Leave your details" callback — only on the blocking popups.
            The 1/2/3-day warning stays a light non-blocking notice, like before. */}
        {isBlocking && (
        <div className="trial-contact-callback">
          {submitted ? (
            <div className="trial-contact-success">
              <CheckCircle size={22} weight="fill" />
              <span>תודה! ניצור איתך קשר בהקדם</span>
            </div>
          ) : (
            <>
              <div className="trial-contact-callback-divider">
                <span>או השאירו פרטים ואנחנו נחזור אליכם</span>
              </div>
              <textarea
                className="trial-contact-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="מתי נוח שניצור קשר? משהו שכדאי שנדע? (לא חובה)"
                rows={2}
                dir="rtl"
                disabled={isLoading}
              />
              {submitError && <p className="trial-contact-error">{submitError}</p>}
              <button
                type="button"
                className="trial-contact-btn trial-contact-btn-callback"
                onClick={handleCallbackSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <PaperPlaneTilt size={18} weight="fill" />
                )}
                <span>{isLoading ? "שולח..." : "צרו איתי קשר"}</span>
              </button>
            </>
          )}
        </div>
        )}

        <div className="trial-contact-info">
          <div className="trial-contact-info-item">
            <Phone size={14} />
            <span>{SUPPORT_PHONE_DISPLAY}</span>
          </div>
          <span className="trial-contact-info-sep">|</span>
          <div className="trial-contact-info-item">
            <Envelope size={14} />
            <span>{SUPPORT_EMAIL}</span>
          </div>
        </div>

        {isBlocking ? (
          <button onClick={handleLogout} className="trial-contact-logout" type="button">
            <SignOut size={14} weight="regular" />
            <span>התנתק מהמערכת</span>
          </button>
        ) : (
          <button className="trial-contact-dismiss" onClick={onAcknowledge}>
            הבנתי, אמשיך מאוחר יותר
          </button>
        )}
      </div>
    </div>
  );
}

export default TrialContactModal;
